import { useState, useCallback, useRef } from 'react';
import { getFirebase, hasFirebaseConfig } from '../firebase';

const LS_PROFILE_KEY = 'touchstone_profile';
const LS_ONBOARDED_KEY = 'touchstone_onboarded';

function readLocalProfile() {
  try {
    const raw = localStorage.getItem(LS_PROFILE_KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  return null;
}

function writeLocalProfile(profile) {
  try {
    localStorage.setItem(LS_PROFILE_KEY, JSON.stringify(profile));
    localStorage.setItem(LS_ONBOARDED_KEY, 'true');
  } catch {}
}

function clearLocalProfile() {
  try {
    localStorage.removeItem(LS_PROFILE_KEY);
    localStorage.removeItem(LS_ONBOARDED_KEY);
    localStorage.removeItem('touchstone_email');
  } catch {}
}

/**
 * Hook for managing user profile with Firestore persistence + localStorage cache.
 * 
 * - On sign-in: loads from Firestore, caches to localStorage
 * - On save: writes to both Firestore and localStorage
 * - Falls back to localStorage if Firestore is unreachable
 */
export function useProfile() {
  const [profile, setProfile] = useState(() => readLocalProfile());
  const [profileLoading, setProfileLoading] = useState(false);
  const loadedUidRef = useRef(null);

  /**
   * Load profile from Firestore for a given user.
   * Called when auth state changes (user signs in).
   */
  const loadProfile = useCallback(async (uid) => {
    if (!uid || loadedUidRef.current === uid) return profile;

    // Start with localStorage for instant hydration
    const localData = readLocalProfile();
    if (localData) setProfile(localData);

    if (!hasFirebaseConfig) {
      loadedUidRef.current = uid;
      return localData;
    }

    setProfileLoading(true);
    try {
      const fb = await getFirebase();
      if (!fb) {
        loadedUidRef.current = uid;
        setProfileLoading(false);
        return localData;
      }

      const docRef = fb.doc(fb.db, 'users', uid);
      const docSnap = await fb.getDoc(docRef);

      if (docSnap.exists()) {
        const firestoreProfile = docSnap.data();
        setProfile(firestoreProfile);
        writeLocalProfile(firestoreProfile);
        loadedUidRef.current = uid;
        setProfileLoading(false);
        return firestoreProfile;
      } else if (localData) {
        // Profile exists locally but not in Firestore — migrate it up
        await fb.setDoc(docRef, { ...localData, migratedAt: new Date().toISOString() }, { merge: true });
        loadedUidRef.current = uid;
        setProfileLoading(false);
        return localData;
      }

      loadedUidRef.current = uid;
      setProfileLoading(false);
      return null;
    } catch (err) {
      console.warn('Failed to load profile from Firestore:', err.message);
      loadedUidRef.current = uid;
      setProfileLoading(false);
      return localData;
    }
  }, [profile]);

  /**
   * Save profile to both Firestore and localStorage.
   */
  const saveProfile = useCallback(async (uid, newProfile) => {
    setProfile(newProfile);
    writeLocalProfile(newProfile);

    if (!uid || !hasFirebaseConfig) return;

    try {
      const fb = await getFirebase();
      if (!fb) return;

      const docRef = fb.doc(fb.db, 'users', uid);
      await fb.setDoc(docRef, {
        ...newProfile,
        updatedAt: new Date().toISOString(),
      }, { merge: true });
    } catch (err) {
      console.warn('Failed to save profile to Firestore:', err.message);
    }
  }, []);

  /**
   * Clear profile from state, localStorage, and reset loaded uid.
   */
  const clearProfile = useCallback(() => {
    setProfile(null);
    clearLocalProfile();
    loadedUidRef.current = null;
  }, []);

  /**
   * Check if user has completed onboarding.
   */
  const isOnboarded = profile !== null && Object.keys(profile).length > 0;

  return {
    profile,
    profileLoading,
    isOnboarded,
    loadProfile,
    saveProfile,
    clearProfile,
  };
}

export default useProfile;
